import { useMemo } from 'react';
import { Stack } from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';

import useCooperativeId from 'shared/hooks/useCooperativeId';
import formatBytes from 'shared/utils/formatBytes';

import { fileLoaderApi } from '../../api';
import type { Document, EntityType } from '../../model';

import { Styled } from './FilesList.styled';

type FileItemProps = {
  document: Document;
  onDelete: (documentId: string) => void;
};

const FileItem = ({ document, onDelete }: FileItemProps) => {
  const handleDeleteDocument = () => {
    onDelete(document.Id);
  };

  return (
    <Styled.ListItem>
      <Styled.TextOverflow>
        <Styled.FileName>{document.Name}</Styled.FileName>
      </Styled.TextOverflow>
      <Stack alignItems="center" direction="row" spacing={1}>
        <Styled.FileMetadata>{formatBytes(document.Size)}</Styled.FileMetadata>
        {document.CanDelete && (
          <Styled.IconButton onClick={handleDeleteDocument}>
            <Styled.ClearIcon color="primary" />
          </Styled.IconButton>
        )}
      </Stack>
    </Styled.ListItem>
  );
};

type MixedFilesListProps = {
  documentsToBeDeleted: string[];
  entityType: EntityType;
  entityId: string | null;
  onAddDocumentToDeletionQueue: (documentId: string) => void;
};

const MixedFilesList = ({
  documentsToBeDeleted,
  entityId,
  entityType,
  onAddDocumentToDeletionQueue,
}: MixedFilesListProps) => {
  const cooperativeId = useCooperativeId();

  const { documents: rawDocuments } = fileLoaderApi.useGetAttachmentsQuery(
    cooperativeId && entityId
      ? {
          cooperativeId,
          entityId,
          entityType,
        }
      : skipToken,
    {
      selectFromResult: (res) => ({
        documents: res.data ?? [],
      }),
    }
  );

  const documents = useMemo(() => {
    const res: Document[] = [];

    for (const document of rawDocuments) {
      if (document.CanDelete && !documentsToBeDeleted.includes(document.Id)) {
        res.push(document);
      }
    }

    return res;
  }, [rawDocuments, documentsToBeDeleted]);

  if (!documents.length) return null;

  return (
    <Styled.List>
      {documents.map((document) => (
        <FileItem
          key={document.Id}
          document={document}
          onDelete={onAddDocumentToDeletionQueue}
        />
      ))}
    </Styled.List>
  );
};

export default MixedFilesList;
